import { useState, useRef, useEffect, type FormEvent, type ReactNode } from 'react';
import { Send, Sparkles, Loader2, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface AiAssistatProps {
  /** Header label */
  title?: string;
  /** Empty-state heading (defaults to title) */
  emptyHeading?: string;
  /** Empty-state body */
  description?: string;
  className?: string;
  /** Must return the assistant reply (e.g. Flow `callFlowChat`) */
  onSend: (message: string) => Promise<string>;
  /** Extra header controls (e.g. close Flow panel) */
  headerTrailing?: ReactNode;
  /** Replaces default sparkles in the header (e.g. Flow orb) */
  leadingIcon?: ReactNode;
  /** Replaces default sparkles in the empty state */
  emptyStateIcon?: ReactNode;
  /**
   * Hide title/header row (e.g. inside Flow widget where branding is already above).
   * Fills width with gradient; clear control moves into the composer row.
   */
  embedded?: boolean;
}

export function AiAssistat({
  title = 'AI Assistant',
  emptyHeading,
  description = "Ask me anything and I'll do my best to assist you!",
  className,
  onSend,
  headerTrailing,
  leadingIcon,
  emptyStateIcon,
  embedded = false,
}: AiAssistatProps) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<{ text: string; isUser: boolean }[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const heading = emptyHeading ?? title;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = input.trim();
    if (trimmed === '' || isTyping) return;

    setMessages((prev) => [...prev, { text: trimmed, isUser: true }]);
    setInput('');
    setIsTyping(true);

    void (async () => {
      try {
        const reply = await onSend(trimmed);
        setMessages((prev) => [...prev, { text: reply, isUser: false }]);
      } catch {
        setMessages((prev) => [
          ...prev,
          { text: 'Something went wrong. Please try again.', isUser: false },
        ]);
      } finally {
        setIsTyping(false);
      }
    })();
  };

  const clearChat = () => {
    setMessages([]);
    setInput('');
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col overflow-hidden',
        'h-full min-h-0 max-h-full',
        embedded
          ? 'min-h-[200px] rounded-none border-0 bg-gradient-to-br from-slate-900 to-indigo-950 shadow-none'
          : 'min-h-[280px] max-w-xl rounded-xl border border-indigo-500/20 bg-gradient-to-br from-slate-900 to-indigo-950 shadow-2xl',
        className
      )}
    >
      {!embedded && (
        <div className="flex shrink-0 items-center justify-between border-b border-indigo-500/30 bg-indigo-600/30 p-3 backdrop-blur-sm sm:p-4">
          <div className="flex min-w-0 items-center gap-2">
            {leadingIcon ?? (
              <Sparkles className="h-5 w-5 shrink-0 text-indigo-300" aria-hidden />
            )}
            <h2 className="truncate text-sm font-medium text-white sm:text-base">{title}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {headerTrailing}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="text-indigo-200 hover:bg-indigo-500/20 hover:text-white"
              onClick={clearChat}
              aria-label="Clear conversation"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto p-3 sm:p-4',
          embedded ? 'bg-slate-900/40' : 'bg-slate-900/50'
        )}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {messages.length === 0 ? (
          <div className="flex h-full min-h-[12rem] flex-col items-center justify-center px-2 text-center">
            {emptyStateIcon ?? (
              <Sparkles
                className="mb-3 h-10 w-10 text-indigo-400 sm:mb-4 sm:h-12 sm:w-12"
                aria-hidden
              />
            )}
            <h3 className="mb-2 text-lg text-indigo-200 sm:text-xl">{heading}</h3>
            <p className="max-w-xs text-xs text-slate-400 sm:text-sm">{description}</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {messages.map((msg, index) => (
              <div
                key={`${msg.isUser}-${index}-${msg.text.slice(0, 24)}`}
                className={cn('flex', msg.isUser ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'animate-fade-in max-w-[85%] rounded-2xl p-2.5 sm:max-w-[80%] sm:p-3',
                    msg.isUser
                      ? 'rounded-tr-none bg-indigo-600 text-white'
                      : 'rounded-tl-none border border-slate-600/50 bg-slate-700/60 text-slate-100'
                  )}
                >
                  {msg.isUser ? (
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  ) : (
                    <div className="prose prose-sm max-w-none text-slate-100 prose-p:my-1 prose-p:leading-relaxed prose-headings:my-1.5 prose-headings:text-slate-50 prose-strong:text-white prose-a:text-indigo-300 prose-code:rounded-sm prose-code:bg-slate-800/80 prose-code:px-1 prose-code:text-indigo-200">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl rounded-tl-none border border-slate-600/50 bg-slate-700/60 p-3 text-slate-100">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 animate-pulse rounded-full bg-indigo-400"
                      style={{ animationDelay: '0ms' }}
                    />
                    <span
                      className="h-2 w-2 animate-pulse rounded-full bg-indigo-400"
                      style={{ animationDelay: '0.2s' }}
                    />
                    <span
                      className="h-2 w-2 animate-pulse rounded-full bg-indigo-400"
                      style={{ animationDelay: '0.4s' }}
                    />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form
        onSubmit={handleSubmit}
        className={cn(
          'shrink-0 border-t border-indigo-500/20 p-3 transition-colors duration-200 sm:p-4',
          embedded && 'bg-indigo-950/50',
          isFocused
            ? 'border-indigo-500/50 bg-slate-800/90'
            : 'border-slate-700/50 bg-slate-800/40'
        )}
      >
        <div className={cn('flex items-center gap-2', embedded && 'gap-1.5')}>
          {embedded && (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="shrink-0 text-indigo-200 hover:bg-indigo-500/20 hover:text-white"
              onClick={clearChat}
              aria-label="Clear conversation"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <div className="relative min-w-0 flex-1">
            <Input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Type your message..."
              disabled={isTyping}
              className="h-11 w-full rounded-full border-slate-600/50 bg-slate-700/50 pr-12 pl-4 text-sm text-white placeholder:text-slate-400 focus-visible:ring-indigo-500/70 md:text-sm"
              aria-label="Message"
            />
            <Button
              type="submit"
              size="icon"
              disabled={input.trim() === '' || isTyping}
              className={cn(
                'absolute right-1 top-1/2 size-9 shrink-0 -translate-y-1/2 rounded-full',
                input.trim() === '' || isTyping
                  ? 'pointer-events-none bg-slate-700/50 text-slate-500'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              )}
              aria-label="Send message"
            >
              {isTyping ? <Loader2 className="size-5 animate-spin" /> : <Send className="size-5" />}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** @deprecated Use `AiAssistat` — kept for copy-paste parity with the snippet name */
const AIMessageBar = AiAssistat;

export default AiAssistat;
export { AIMessageBar };
